import type { BrandTypes } from '@ps/platform-message-framework/common/BrandTypes';
import type { Sites } from '@ps/platform-message-framework/common/Sites';
import { WebTokenType } from '@ps/platform-message-framework/webtokenserver/WebTokenType';
import { ResolverTag } from '@ps/web-core-enums';
import { DebugKey, canUseLocalStorage, createDebug, tryParse } from '@ps/web-core-utils';
import * as Sentry from '@sentry/react';
import { get, identity, mapValues, pickBy } from 'lodash';
import type { Selector } from 'reselect';
import Url from 'url-parse';
import { z } from 'zod';

import { select } from '../../../index';
import type { EffectParams, Model, RootState, SelectorCreator, Slicer } from '../../types';

const ResolvedTagSchema = z.object({
  url: z.string(),
  token: z.string().optional(),
  signature: z.string().optional()
});

const ResolverStateSchema = z.record(z.string(), ResolvedTagSchema);

export type ResolvedTag = z.infer<typeof ResolvedTagSchema>;

export type ResolverState = z.infer<typeof ResolverStateSchema>;

export interface FetchTagPayload {
  tag: string;
  path?: string;
  open?: boolean;
  query?: object;
  tokenType?: number;
  brand?: BrandTypes;
  site?: Sites;
  license?: number;
}

interface FetchBody {
  url: string;
  parameters: {
    [key: string]: string;
  };
}

export const resolverTokenType = {
  [ResolverTag.AddressChangeSupport]: WebTokenType.CUSTOMER_SUPPORT,
  [ResolverTag.BonusesReleaseAndInstant]: WebTokenType.RATIONAL_WEB_SERVICES,
  [ResolverTag.Cashbonus]: WebTokenType.RATIONAL_WEB_SERVICES,
  [ResolverTag.ContactSupport]: WebTokenType.CUSTOMER_SUPPORT,
  [ResolverTag.FreeSpinBonus]: WebTokenType.RATIONAL_WEB_SERVICES,
  [ResolverTag.FreemiumBuyPlayChips]: WebTokenType.FREEMIUM,
  [ResolverTag.GeoAssist]: WebTokenType.CUSTOMER_SUPPORT,
  [ResolverTag.IceHome]: WebTokenType.ICE,
  [ResolverTag.Ice2Home]: WebTokenType.ICE,
  [ResolverTag.IceLeaderboards]: WebTokenType.RATIONAL_WEB_SERVICES,
  [ResolverTag.InstantBonus]: WebTokenType.RATIONAL_WEB_SERVICES,
  [ResolverTag.ISPFreeBets]: WebTokenType.RATIONAL_WEB_SERVICES,
  [ResolverTag.LiveChatSupport]: WebTokenType.CUSTOMER_SUPPORT,
  [ResolverTag.MinigameTicket]: WebTokenType.RATIONAL_WEB_SERVICES,
  [ResolverTag.PlayTheDeal]: WebTokenType.WEB_ACCOUNT_SERVICE,
  [ResolverTag.VerifyMeAML]: WebTokenType.CUSTOMER_SUPPORT,
  [ResolverTag.WebRealMoneyForm]: WebTokenType.CASHIER,
  [ResolverTag.BonusWheelGame]: WebTokenType.WEB_ACCOUNT_SERVICE,
  [ResolverTag.MatchDayMillions]: WebTokenType.WEB_ACCOUNT_SERVICE,
  [ResolverTag.OpenBankidValidation]: WebTokenType.CASHIER,
  [ResolverTag.PickObjectGame]: WebTokenType.WEB_ACCOUNT_SERVICE,
  [ResolverTag.PMCPaywall]: WebTokenType.FREEMIUM,
  [ResolverTag.RGQuestion]: WebTokenType.CUSTOMER_SUPPORT,
  [ResolverTag.SingleReelGame]: WebTokenType.WEB_ACCOUNT_SERVICE,
  [ResolverTag.WC2FEBalance]: WebTokenType.CASHIER,
  [ResolverTag.Withdrawal]: WebTokenType.CASHIER,
  [ResolverTag.WC2FEConvertCurrencies]: WebTokenType.CASHIER,
  [ResolverTag.WC2FEDeposit]: WebTokenType.CASHIER,
  [ResolverTag.WC2FEHistory]: WebTokenType.CASHIER,
  [ResolverTag.WC2FEHome]: WebTokenType.CASHIER,
  [ResolverTag.WC2FEPlayMoney]: WebTokenType.CASHIER,
  [ResolverTag.WC2FEPreferredCurrency]: WebTokenType.CASHIER
};

const localStorageOverride = canUseLocalStorage && localStorage.getItem('starsweb-resolvers');
const overrides = ResolverStateSchema.safeParse(tryParse(localStorageOverride) ?? {});

if (!overrides.success) {
  const debug = createDebug(DebugKey.Overrides);
  debug('Could not parse "starsweb-resolvers" localStorage item: ', overrides.error);
}

export const reducers = {
  response: (state: ResolverState, payload: ResolverState) => ({
    ...state,
    ...payload,
    ...(overrides.success ? overrides.data : {})
  })
};

export const effects: {
  fetchTag(
    { tokenType, tag, query, site }: FetchTagPayload,
    { dispatch, request, rootState }: EffectParams
  ): Promise<void>;
} = {
  async fetchTag({ tokenType, tag, query = {}, site }, { dispatch, request, rootState }) {
    const brand = select.cms.getBrandId(rootState).toString();
    try {
      const { body } = await request<FetchBody>({
        service: 'tags.resolve',
        headers: pickBy({ brand, site }, identity),
        body: pickBy(
          {
            tag,
            tokenType:
              tokenType === undefined
                ? resolverTokenType[tag as keyof typeof resolverTokenType]
                : tokenType,
            urlParam: Url.qs.stringify(query)
          },
          identity
        )
      });

      const url = new Url(
        body.url.startsWith('external-') ? body.url.substring(9) : body.url,
        true
      );
      if (body.parameters) {
        body.parameters = mapValues(body.parameters, (parameter) => encodeURIComponent(parameter));
      }
      url.set('query', { ...query, ...body.parameters, ...url.query });
      dispatch.resolver.response({ [tag]: { url: url.toString(), ...body.parameters } });
    } catch (err) {
      // TODO handle error
      Sentry.captureException(err);
    }
  }
};

const selectors = (slice: Slicer<ResolverState>, createSelector: SelectorCreator) => ({
  get() {
    return slice;
  },
  getTag() {
    return createSelector(
      slice,
      (rootState: RootState, tag: string) => tag,
      (state: ResolverState, tag: string) => state[tag]
    );
  },
  getUrl(this: { getTag: Selector<RootState, ResolvedTag | undefined, string[]> }) {
    return createSelector(this.getTag, (tag): string | undefined => get(tag, 'url'));
  }
});

const resolver: Model<ResolverState, typeof reducers, void, typeof effects, typeof selectors> = {
  state: {},
  reducers,
  effects,
  selectors
};

export default resolver;
